import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useTenant } from '../../contexts/TenantContext';
import { createRuntimeApiClient } from '../../services/runtimeApiInterceptor';
import { messageFromAxiosError } from '../api/runtimeErrors';

const SurveyRuntimeApiContext = createContext(null);

export function SurveyRuntimeApiProvider({ children }) {
  const { authState } = useOktaAuth();
  const { currentTenant } = useTenant();
  const client = useMemo(
    () => createRuntimeApiClient(authState, currentTenant?.tenantId),
    [authState, currentTenant?.tenantId],
  );

  const apiFetch = useCallback(
    async (path, opts = {}) => {
      const method = (opts.method || 'GET').toUpperCase();
      let data = opts.body;
      if (data && typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          /* keep string for non-JSON bodies */
        }
      }
      try {
        const res = await client.request({
          url: path,
          method,
          data: method === 'GET' || method === 'HEAD' ? undefined : data,
          headers: opts.headers,
        });
        if (res.status === 204) return undefined;
        return res.data;
      } catch (e) {
        throw messageFromAxiosError(e, 'Request failed');
      }
    },
    [client],
  );

  const value = useMemo(() => ({ apiFetch, client }), [apiFetch, client]);

  return (
    <SurveyRuntimeApiContext.Provider value={value}>
      {children}
    </SurveyRuntimeApiContext.Provider>
  );
}

export function useSurveyRuntimeApi() {
  const ctx = useContext(SurveyRuntimeApiContext);
  if (!ctx) {
    throw new Error('useSurveyRuntimeApi must be used within SurveyRuntimeApiProvider');
  }
  return ctx;
}
